// components/RetailerProfileModal.jsx
import React, { useState, useEffect } from 'react';
import { apiCall } from '../utils/api';
import toast from 'react-hot-toast';

const RetailerProfileModal = ({ isOpen, onClose, userId }) => {
    const [loading, setLoading] = useState(false);
    const [profileData, setProfileData] = useState(null);
    const [margin, setMarginData] = useState(null);
    const [userList, setUserList] = useState(null);
    const [error, setError] = useState(null);
    const [selectedStatus, setSelectedStatus] = useState('');
    const [selectedRole, setSelectedRole] = useState('');
    const [selectedPlan, setSelectedPlan] = useState('');
    const [selectedPlanLabel, setSelectedPlanLabel] = useState('');
    const [selectedAgent, setSelectedAgent] = useState('');
    const [updating, setUpdating] = useState(false);
    const [activeTab, setActiveTab] = useState('basic');
    const [editingField, setEditingField] = useState(null);
    const [editValue, setEditValue] = useState('');

    useEffect(() => {
        if (isOpen && userId) {
            resetStates();
            fetchRetailerProfile();
            fetchMarginPlan();
            fetchAgentID();
        }
    }, [isOpen, userId]);

    const resetStates = () => {
        setProfileData(null);
        setSelectedStatus('');
        setSelectedRole('');
        setSelectedPlan('');
        setSelectedPlanLabel('');
        setSelectedAgent('');
        setEditingField(null);
        setEditValue('');
        setError(null);
        setMarginData(null);
        setUserList(null);
    };

    // Helper functions to map role and status
    const getRole = (role) => {
        switch (String(role)) {
            case "1": return "Admin";
            case "2": return "Asst. Admin";
            case "3": return "Employee";
            case "4": return "Shareholder";
            case "5": return "Retailer";
            case "6": return "API";
            case "7": return "Agent";
            default: return "Error Account";
        }
    };

    const getRoleId = (roleName) => {
        const roleMap = {
            'Admin': '1',
            'Asst. Admin': '2',
            'Employee': '3',
            'Shareholder': '4',
            'Retailer': '5',
            'API': '6',
            'Agent': '7'
        };
        return roleMap[roleName] || roleName;
    };

    const accountStatus = (status) => {
        switch (String(status)) {
            case "1": return "Regular";
            case "2": return "Blocked";
            case "3": return "Suspended";
            case "4": return "Deleted";
            case "5": return "Debit Freeze";
            case "6": return "Pending";
            default: return "Error Account";
        }
    };

    const getStatusId = (statusName) => {
        const statusMap = {
            'Regular': '1',
            'Blocked': '2',
            'Suspended': '3',
            'Deleted': '4',
            'Debit Freeze': '5',
            'Pending': '6'
        };
        return statusMap[statusName] || statusName;
    };

    const fetchRetailerProfile = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await apiCall(
                "retailer-profile",
                {
                    user_id: userId
                },
                true
            );

            if (response.status === true) {
                setProfileData(response.data);
                if (response.data) {
                    // Status mapping
                    const statusValue = response.data.status || response.data.status_id;
                    const mappedStatus = isNaN(statusValue) ? statusValue : accountStatus(statusValue);
                    setSelectedStatus(mappedStatus);

                    // Role mapping
                    const roleValue = response.data.role || response.data.role_id;
                    const mappedRole = isNaN(roleValue) ? roleValue : getRole(roleValue);
                    setSelectedRole(mappedRole);

                    // Plan mapping
                    if (response.data.plan_name) {
                        setSelectedPlanLabel(response.data.plan_name);
                        if (margin && margin.length > 0) {
                            const matchedPlan = margin.find(plan => plan.name === response.data.plan_name);
                            if (matchedPlan) {
                                setSelectedPlan(matchedPlan.plan_id);
                            } else {
                                setSelectedPlan('');
                            }
                        }
                    }

                    // Agent mapping - FIXED: Set selectedAgent from profile data
                    if (response.data.agent_id && response.data.agent_id !== null && response.data.agent_id !== '') {
                        setSelectedAgent(String(response.data.agent_id));
                    } else {
                        setSelectedAgent('');
                    }
                }
            } else {
                setError(response.message || "Failed to fetch retailer profile");
                toast.error(response.message || "Failed to fetch retailer profile");
            }
        } catch (error) {
            setError("Network error. Please try again.");
            toast.error("Network error. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const fetchMarginPlan = async () => {
        try {
            const response = await apiCall(
                "margin-plan",
                {
                    limit: 1000
                },
                true
            );

            if (response.status === true) {
                setMarginData(response.data);
                // If we have profile data and plan_name, find the matching plan
                if (profileData && profileData.plan_name) {
                    const matchedPlan = response.data.find(plan => plan.name === profileData.plan_name);
                    if (matchedPlan) {
                        setSelectedPlan(matchedPlan.plan_id);
                        setSelectedPlanLabel(profileData.plan_name);
                    }
                }
            }
        } catch (error) {
            console.error("Error fetching margin plans:", error);
        }
    };

    const fetchAgentID = async () => {
        try {
            const response = await apiCall(
                "all-users",
                {
                    limit: 10000,
                    role: '7'
                },
                true
            );

            if (response.status === true) {
                setUserList(response.data);
                // FIXED: Use profileData from state, not from closure
                // Since profileData might not be available yet, we'll set it after userList is loaded
                // and let the useEffect or render handle the selection
            }
        } catch (error) {
            console.error("Error fetching agent list:", error);
        }
    };

    // Generic update function for all fields
    const handleUpdate = async (config) => {
        const { 
            field, 
            value, 
            payload, 
            successMessage, 
            onSuccess 
        } = config;

        // Validation
        if (config.required !== false) {
            if (!value || (typeof value === 'string' && value.trim() === '')) {
                toast.error(`${field} cannot be empty`);
                return;
            }
        }

        setUpdating(true);
        try {
            const response = await apiCall(
                'user-update',
                {
                    user_id: profileData.user_id,
                    ...payload
                },
                true
            );

            if (response.status === true) {
                toast.success(successMessage || `${field} updated successfully!`);
                if (onSuccess) {
                    onSuccess(value);
                }
                fetchRetailerProfile();
            } else {
                toast.error(response.message || `Failed to update ${field}`);
            }
        } catch (error) {
            toast.error("Network error. Please try again.");
        } finally {
            setUpdating(false);
        }
    };

    // Inline field edit handlers
    const startEditing = (field, value) => {
        setEditingField(field);
        setEditValue(value || '');
    };

    const cancelEditing = () => {
        setEditingField(null);
        setEditValue('');
    };

    const saveField = async (field, value) => {
        const fieldMap = {
            'Full Name': 'name',
            'Email': 'email',
            'IP Address': 'ip',
            'Recharge Callback': 'recharge_callback',
            'Payment Callback': 'payment_callback'
        };

        const apiField = fieldMap[field] || field.toLowerCase();

        await handleUpdate({
            field: field,
            value: value,
            payload: {
                [apiField]: value.trim()
            },
            successMessage: `${field} updated successfully!`,
            onSuccess: () => {
                setEditingField(null);
                setEditValue('');
            }
        });
    };

    const handleStatusUpdate = async (newStatus) => {
        const statusId = getStatusId(newStatus);
        
        await handleUpdate({
            field: "Status",
            value: newStatus,
            payload: {
                status: statusId
            },
            successMessage: "Status updated successfully!",
            onSuccess: () => {
                setSelectedStatus(newStatus);
            }
        });
    };

    const handleRoleUpdate = async (newRole) => {
        const roleId = getRoleId(newRole);
        
        await handleUpdate({
            field: "Role",
            value: newRole,
            payload: {
                role: roleId
            },
            successMessage: "Role updated successfully!",
            onSuccess: () => {
                setSelectedRole(newRole);
            }
        });
    };

    const handlePlanUpdate = async (planId) => {
        await handleUpdate({
            field: "Plan",
            value: planId,
            payload: {
                plan_id: planId
            },
            successMessage: "Plan updated successfully!",
            required: false,
            onSuccess: (value) => {
                const matchedPlan = margin.find(plan => plan.plan_id === value);
                if (matchedPlan) {
                    setSelectedPlanLabel(matchedPlan.name);
                }
                setSelectedPlan(value);
            }
        });
    };

    const handleAgentUpdate = async (agentId) => {
        const agentValue = agentId || null;
        
        await handleUpdate({
            field: "Agent ID",
            value: agentValue,
            payload: {
                agent_id: agentValue
            },
            successMessage: "Agent ID updated successfully!",
            required: false,
            onSuccess: () => {
                setSelectedAgent(agentId || '');
            }
        });
    };

    if (!isOpen) return null;

    const getStatusBadge = (status) => {
        const statusMap = {
            'Regular': 'bg-green-100 text-green-800',
            'Active': 'bg-green-100 text-green-800',
            'Blocked': 'bg-red-100 text-red-800',
            'Suspended': 'bg-yellow-100 text-yellow-800',
            'Deleted': 'bg-gray-100 text-gray-800',
            'Debit Freeze': 'bg-purple-100 text-purple-800',
            'Pending': 'bg-blue-100 text-blue-800',
            'Error Account': 'bg-red-100 text-red-800'
        };
        return statusMap[status] || 'bg-gray-100 text-gray-800';
    };

    const getRoleBadge = (role) => {
        const roleMap = {
            'Admin': 'bg-purple-100 text-purple-800',
            'Asst. Admin': 'bg-indigo-100 text-indigo-800',
            'Employee': 'bg-blue-100 text-blue-800',
            'Shareholder': 'bg-pink-100 text-pink-800',
            'Retailer': 'bg-green-100 text-green-800',
            'API': 'bg-cyan-100 text-cyan-800',
            'Agent': 'bg-yellow-100 text-yellow-800',
            'Error Account': 'bg-red-100 text-red-800'
        };
        return roleMap[role] || 'bg-gray-100 text-gray-800';
    };

    const getVerificationStatus = (status) => {
        return status === '1' ? (
            <span className="inline-flex items-center text-green-600">
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
                Verified
            </span>
        ) : (
            <span className="inline-flex items-center text-red-600">
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
                Not Verified
            </span>
        );
    };

    const tabs = [
        { id: 'basic', label: 'Basic Information' },
        { id: 'account', label: 'Account Details' },
        { id: 'verification', label: 'Verification' },
        { id: 'security', label: 'Security' }
    ];

    const renderEditableField = (field, value) => {
        if (editingField === field) {
            return (
                <div className="flex items-center gap-2">
                    <input
                        type="text"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        className="px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent w-48"
                        autoFocus
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                saveField(field, editValue);
                            }
                            if (e.key === 'Escape') {
                                cancelEditing();
                            }
                        }}
                        disabled={updating}
                    />
                    <button
                        onClick={() => saveField(field, editValue)}
                        disabled={updating}
                        className="p-1 text-green-600 hover:text-green-800 transition-colors disabled:opacity-50"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                    </button>
                    <button
                        onClick={cancelEditing}
                        disabled={updating}
                        className="p-1 text-red-600 hover:text-red-800 transition-colors disabled:opacity-50"
                    >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>
            );
        }

        return (
            <div className="flex items-center gap-2">
                <span className="text-sm text-gray-900">{value || 'N/A'}</span>
                <button
                    onClick={() => startEditing(field, value)}
                    className="text-blue-600 hover:text-blue-800 transition-colors"
                    title={`Edit ${field}`}
                >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                    </svg>
                </button>
            </div>
        );
    };

    const renderContent = () => {
        if (loading) {
            return (
                <div className="flex flex-col items-center justify-center py-20">
                    <div className="relative">
                        <div className="h-12 w-12 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin"></div>
                    </div>
                    <span className="mt-4 text-sm font-medium text-gray-600 animate-pulse">
                        Loading...
                    </span>
                </div>
            );
        }

        if (error) {
            return (
                <div className="text-center py-20">
                    <svg className="w-16 h-16 text-red-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-red-600">{error}</p>
                    <button
                        onClick={fetchRetailerProfile}
                        className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        Try Again
                    </button>
                </div>
            );
        }

        if (!profileData) {
            return (
                <div className="text-center py-20">
                    <p className="text-gray-500">No data available</p>
                </div>
            );
        }

        switch (activeTab) {
            case 'basic':
                return renderBasicInformation();
            case 'account':
                return renderAccountDetails();
            case 'verification':
                return renderVerification();
            case 'security':
                return renderSecurity();
            default:
                return null;
        }
    };

    const renderBasicInformation = () => {
        const fields = [
            { key: 'User ID', value: profileData.user_id, editable: false },
            { key: 'Full Name', value: profileData.name, editable: true },
            { key: 'Mobile', value: profileData.mobile, editable: false },
            { key: 'Email', value: profileData.email, editable: true },
            { key: 'IP Address', value: profileData.ip, editable: true },
            { key: 'Registered Date', value: profileData.date, editable: false },
            { key: 'Registered Time', value: profileData.time, editable: false },
            { key: 'Recharge Callback', value: profileData.recharge_callback, editable: true },
            { key: 'Payment Callback', value: profileData.payment_callback, editable: true }
        ];

        return (
            <div className="space-y-4">
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Field</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {fields.map(({ key, value, editable }) => (
                                <tr key={key}>
                                    <td className="px-4 py-3 text-sm font-medium text-gray-700">{key}</td>
                                    <td className="px-4 py-3 text-sm">
                                        {editable ? renderEditableField(key, value) : (value || 'N/A')}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    const renderAccountDetails = () => {
        const statusOptions = ['Regular', 'Blocked', 'Suspended', 'Deleted', 'Debit Freeze', 'Pending'];
        const statusExists = statusOptions.some(status => status === selectedStatus);

        const roleOptions = ['Admin', 'Asst. Admin', 'Employee', 'Shareholder', 'Retailer', 'API', 'Agent'];
        const roleExists = roleOptions.some(role => role === selectedRole);

        let currentPlanId = selectedPlan;
        if (margin && profileData && profileData.plan_name) {
            const matchedPlan = margin.find(plan => plan.name === profileData.plan_name);
            if (matchedPlan) {
                currentPlanId = matchedPlan.plan_id;
                if (selectedPlan !== currentPlanId) {
                    setSelectedPlan(currentPlanId);
                }
            }
        }
        const planExists = margin && margin.some(plan => plan.plan_id === String(currentPlanId));
        
        // FIXED: Check if selectedAgent exists in userList
        const agentExists = userList && selectedAgent && userList.some(user => String(user.user_id) === String(selectedAgent));

        const fields = [
            {
                key: 'Balance',
                value: `₹${parseFloat(profileData.balance || 0).toFixed(2)}`,
                editable: false,
                className: 'font-bold text-green-600'
            },
            {
                key: 'Role',
                value: selectedRole,
                editable: true,
                isSelect: true,
                selectOptions: roleOptions,
                onSelectChange: handleRoleUpdate,
                currentValue: selectedRole,
                exists: roleExists
            },
            {
                key: 'Status',
                value: selectedStatus,
                editable: true,
                isSelect: true,
                selectOptions: statusOptions,
                onSelectChange: handleStatusUpdate,
                currentValue: selectedStatus,
                exists: statusExists
            },
            { key: 'Referral Code', value: profileData.refer_code, editable: false },
            {
                key: 'Plan Name',
                value: profileData.plan_name || 'No Plan',
                editable: true,
                isSelect: true,
                selectOptions: margin ? margin.map(plan => ({ value: plan.plan_id, label: plan.name })) : [],
                onSelectChange: handlePlanUpdate,
                currentValue: currentPlanId,
                exists: planExists,
                defaultOption: margin && margin.length > 0 ? margin[0].plan_id : ''
            },
            {
                key: 'Agent ID',
                value: profileData.agent_id || 'No Agent',
                editable: true,
                isSelect: true,
                selectOptions: userList ? userList.map(user => ({ value: user.user_id, label: `${user.name} (${user.user_id})` })) : [],
                onSelectChange: handleAgentUpdate,
                currentValue: selectedAgent,
                exists: agentExists,
                defaultOption: '',
                allowNull: true
            },
            { key: 'Referral By', value: profileData.refer_by || 'N/A', editable: false },
            { key: 'App ID', value: profileData.app_id, editable: false },
            { key: 'Last Balance Update', value: profileData.last_balance_update, editable: false }
        ];

        return (
            <div className="space-y-4">
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Field</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {fields.map(({ key, value, editable, isSelect, selectOptions, onSelectChange, currentValue, exists, defaultOption, allowNull, className }) => (
                                <tr key={key}>
                                    <td className="px-4 py-3 text-sm font-medium text-gray-700">{key}</td>
                                    <td className="px-4 py-3 text-sm">
                                        {isSelect ? (
                                            <select
                                                value={
                                                    key === 'Agent ID' && (!currentValue || currentValue === '')
                                                        ? ''
                                                        : exists
                                                            ? currentValue
                                                            : (defaultOption || '')
                                                }
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    if (key === 'Agent ID' && val === '') {
                                                        onSelectChange(null);
                                                    } else {
                                                        onSelectChange(val);
                                                    }
                                                }}
                                                className="px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                                disabled={updating || !selectOptions.length}
                                            >
                                                <option value="" disabled>
                                                    -- select --
                                                </option>
                                                {selectOptions.map((option, idx) => (
                                                    <option
                                                        key={idx}
                                                        value={typeof option === 'object' ? option.value : option}
                                                    >
                                                        {typeof option === 'object' ? option.label : option}
                                                    </option>
                                                ))}
                                            </select>
                                        ) : editable ? (
                                            renderEditableField(key, value)
                                        ) : (
                                            <span className={className || 'text-gray-900'}>{value || 'N/A'}</span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    const renderVerification = () => {
        const data = {
            'Mobile Verification': getVerificationStatus(profileData.mobile_verification),
            'Email Verification': getVerificationStatus(profileData.email_verification),
            'KYC Verification': getVerificationStatus(profileData.kyc_verification)
        };

        return (
            <div className="space-y-4">
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Field</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {Object.entries(data).map(([key, value]) => (
                                <tr key={key}>
                                    <td className="px-4 py-3 text-sm font-medium text-gray-700">{key}</td>
                                    <td className="px-4 py-3 text-sm">{value}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    const renderSecurity = () => {
        const securityData = {
            'Checksum': profileData.checksum,
            'OTP': profileData.otp || 'N/A',
            'OTP Token': profileData.otp_token || 'N/A',
            'OTP Time': profileData.otp_time || 'N/A',
            'OTP Type': profileData.otp_type || 'N/A',
            'Token': profileData.token || 'N/A'
        };

        return (
            <div className="space-y-4">
                <div className="overflow-x-auto border border-gray-200 rounded-lg">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Field</th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {Object.entries(securityData).map(([key, value]) => (
                                <tr key={key}>
                                    <td className="px-4 py-3 text-sm font-medium text-gray-700">{key}</td>
                                    <td className="px-4 py-3 text-sm font-mono text-gray-900 break-all">{value || 'N/A'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    };

    return (
        <div className="fixed inset-0 z-50 overflow-y-auto">
            <div
                className="fixed inset-0 bg-black/50 transition-opacity duration-300 ease-in-out"
                onClick={onClose}
            ></div>

            <div className="flex min-h-full items-center justify-center p-4">
                <div className="relative bg-white rounded-2xl shadow-2xl max-w-7xl w-full max-h-[90vh] overflow-hidden flex flex-col transition-all duration-300 ease-in-out">
                    {/* Header */}
                    <div className="flex-shrink-0 sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center z-10">
                        <div>
                            <h3 className="text-xl font-semibold text-gray-900">
                                Retailer Profile
                            </h3>
                            <p className="text-sm text-gray-500">User ID: #{userId}</p>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-lg"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {/* Tabs */}
                    <div className="flex-shrink-0 bg-white border-b border-gray-200 px-6 sticky top-[73px] z-5">
                        <div className="flex space-x-1 overflow-x-auto">
                            {tabs.map((tab) => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={`
                                        px-4 py-3 text-sm font-medium transition-all duration-200 whitespace-nowrap
                                        ${activeTab === tab.id
                                            ? 'text-blue-600 border-b-2 border-blue-600'
                                            : 'text-gray-500 hover:text-gray-700 hover:border-b-2 hover:border-gray-300'
                                        }
                                    `}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto p-6">
                        {renderContent()}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RetailerProfileModal;